import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QrCode, Lock } from 'lucide-react';
import { useSettings } from '../src/contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';
import BetaBanner from './BetaBanner';

/**
 * Страница, где гость сам выбирает стол.
 *
 * Нужна на обкатке, пока QR-коды не напечатаны и не расставлены по залу.
 * В обычной работе выключена администратором: иначе заказ можно оформить на
 * любой занятый стол, не заходя в заведение.
 */
const TableSelect: React.FC = () => {
  const { settings, loading } = useSettings();
  const { language } = useLanguage();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Выключено администратором — говорим прямо, а не делаем вид, что страницы нет.
  if (!settings.tableSelectEnabled) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-[var(--bg-primary)]">
        <div className="w-14 h-14 rounded-2xl bg-[var(--neutral-bg)] flex items-center justify-center">
          <Lock className="w-6 h-6 text-[var(--text-secondary)]" />
        </div>
        <p className="text-base font-black text-[var(--text-primary)] max-w-xs">
          {t('tableSelect.disabled', language)}
        </p>
        <p className="text-sm text-[var(--text-secondary)] max-w-xs">
          {t('tableSelect.disabledHint', language)}
        </p>
      </div>
    );
  }

  const tables = Array.from({ length: settings.tableCount }, (_, i) => String(i + 1));

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <BetaBanner />
      <div className="flex-1 px-5 py-8 max-w-2xl mx-auto w-full">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[var(--accent-10)] flex items-center justify-center">
            <QrCode className="w-5 h-5 text-[var(--accent-color)]" />
          </div>
          <h1 className="text-2xl font-black text-[var(--text-primary)]">
            {t('tableSelect.title', language)}
          </h1>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-6">
          {t('tableSelect.hint', language)}
        </p>

        <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
          {tables.map((n) => (
            <motion.button
              key={n}
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/table/${n}`)}
              aria-label={t('common.tableLabel', language).replace('{n}', n)}
              className="aspect-square rounded-2xl border-2 border-[var(--glass-border)] bg-[var(--bg-card)] text-[var(--text-primary)] font-black text-xl flex items-center justify-center transition-colors hover:border-[var(--accent-color)] hover:text-[var(--accent-color)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-color)]"
            >
              {n}
            </motion.button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => navigate('/takeaway')}
          className="mt-6 w-full py-3.5 rounded-2xl border-2 border-[var(--glass-border)] text-[var(--text-secondary)] font-bold text-sm hover:border-[var(--accent-color)] hover:text-[var(--accent-color)] transition-colors"
        >
          {t('checkout.takeaway', language)}
        </button>
      </div>
    </div>
  );
};

export default TableSelect;
